import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatMenuModule } from "@angular/material/menu";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { SharedModule } from "../../../../shared/shared.module";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { Widget } from "../../models/dashboard.model";
import { LiveProductionService } from "../../../live-production/services/live-production.service";
import { LiveProductionPrefsService } from "../../../live-production/services/live-production-prefs.service";
import { LiveLineRowComponent } from "../../../live-production/components/live-line-row/live-line-row.component";
import { LineLiveSnapshot } from "../../../live-production/models/live-production.model";
import { LiveLineDetailDialogComponent } from "../../../live-production/components/live-line-detail-dialog/live-line-detail-dialog.component";

@Component({
  selector: "vl-widget-live-production",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    MatCheckboxModule,
    MatButtonModule,
    WidgetTitleComponent,
    LiveLineRowComponent,
  ],
  host: { class: "widget-component" },
  templateUrl: "./widget-live-production.component.html",
  styleUrl: "./widget-live-production.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WidgetLiveProductionComponent implements OnInit, OnDestroy {
  widget = input<Widget>();

  service = inject(LiveProductionService);
  prefs = inject(LiveProductionPrefsService);
  dialog = inject(MatDialog);

  /** Toutes les lignes (pour le menu de filtre individuel). */
  allLignes = computed(() => this.service.lignes());

  visibleLignes = computed<LineLiveSnapshot[]>(() => {
    let list = this.service.lignes();
    if (this.prefs.onlyDevices()) {
      list = list.filter((l) => !!l.deviceId);
    }
    if (this.prefs.hideInactive()) {
      list = list.filter(
        (l) => l.state !== "HORS_PLAGE" && l.state !== "INACTIF",
      );
    }
    const hidden = this.prefs.hiddenLigneIds();
    if (hidden.size > 0) {
      list = list.filter((l) => !hidden.has(l.ligneId));
    }
    // Tri : actives en haut, puis EN_ATTENTE, puis HORS_PLAGE/INACTIF
    const order: Record<string, number> = {
      EN_PROD: 0,
      RALENTI: 1,
      DEVICE_OFFLINE: 2,
      EN_ATTENTE: 3,
      HORS_PLAGE: 4,
      INACTIF: 5,
    };
    return [...list].sort((a, b) => {
      const oa = order[a.state] ?? 99;
      const ob = order[b.state] ?? 99;
      if (oa !== ob) return oa - ob;
      return a.ligneDenomination.localeCompare(b.ligneDenomination);
    });
  });

  nbActives = computed(
    () =>
      this.service.lignes().filter((l) => l.state === "EN_PROD").length,
  );
  nbTotal = computed(() => this.service.lignes().length);
  hiddenCount = computed(() => this.prefs.hiddenLigneIds().size);

  loading = computed(() => this.service.loading());

  ngOnInit(): void {
    void this.service.connect();
  }

  ngOnDestroy(): void {
    this.service.disconnect();
  }

  cadenceFor(ligneId: string) {
    return this.service.cadenceFor(ligneId);
  }

  trackByLigne(_: number, l: LineLiveSnapshot) {
    return l.ligneId;
  }

  toggleLigneHidden(ligneId: string, event?: Event): void {
    event?.stopPropagation();
    this.prefs.toggleHidden(ligneId);
  }

  showAllLignes(): void {
    this.prefs.showAll();
  }

  /**
   * Ouvre la page de supervision dans une nouvelle fenêtre popup.
   *
   * Pour forcer une vraie fenêtre détachée (et non un nouvel onglet dans
   * la fenêtre courante, comme Arc/Chrome le font parfois), on s'appuie
   * sur la combinaison la plus universelle : `width`/`height` explicites
   * comme premiers features → c'est le signal historique que Chromium et
   * Firefox utilisent pour basculer en mode popup. On évite `noopener`
   * (certains navigateurs en mode popup-bloqué retombent sur un onglet
   * dans la même fenêtre quand `noopener` est présent).
   */
  openFullscreen(event?: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();
    const w = Math.min(1600, screen.availWidth ?? screen.width);
    const h = Math.min(1000, (screen.availHeight ?? screen.height) - 80);
    const left = Math.max(0, ((screen.availWidth ?? screen.width) - w) / 2);
    const top = Math.max(0, ((screen.availHeight ?? screen.height) - h) / 2);
    const features = [
      `width=${Math.round(w)}`,
      `height=${Math.round(h)}`,
      `left=${Math.round(left)}`,
      `top=${Math.round(top)}`,
      "popup=yes",
      "toolbar=no",
      "menubar=no",
      "location=no",
      "status=no",
      "scrollbars=yes",
      "resizable=yes",
    ].join(",");
    // Nom unique → chaque clic crée une nouvelle fenêtre, pas de réutilisation.
    const target = `live_prod_${Date.now()}`;
    const win = window.open("/live-production", target, features);
    if (!win) {
      alert(
        "Le navigateur a bloqué l'ouverture de la fenêtre de supervision. " +
          "Autorisez les popups pour ce site puis réessayez.",
      );
      return;
    }
    win.focus();
  }

  /**
   * Click sur une ligne → ouvre l'OF dans une **nouvelle** fenêtre popup
   * détachée (pas de réutilisation, pas de navigation de la page parente).
   */
  onLineOpen(ligne: LineLiveSnapshot): void {
    const ofId = ligne.currentOf?.ofId;
    if (ofId) {
      const features = [
        `width=${Math.min(1280, screen.width)}`,
        `height=${Math.min(900, screen.height - 100)}`,
        "popup=yes",
        "toolbar=no",
        "menubar=no",
        "location=no",
        "status=no",
        "scrollbars=yes",
        "resizable=yes",
      ].join(",");
      const w = window.open(
        `/productions/suivi/ope?ofId=${ofId}`,
        `justup_of_${Date.now()}`,
        features,
      );
      if (w) w.focus();
      return;
    }
    // Fallback : si pas d'OF actif, ouvre la fiche ligne via le dialog
    this.dialog.open(LiveLineDetailDialogComponent, {
      data: { ligneId: ligne.ligneId },
      width: "min(90vw, 900px)",
      autoFocus: false,
    });
  }
}
